import { IconName } from '@ardoq/icons';
import { toUpdatedViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import {
  isCompleteFormattingRule,
  perspectiveGroupingRuleToGroupingRule,
} from '@ardoq/perspectives';
import { dispatchAction } from '@ardoq/rxbeach';
import { setTraversalContextComponent } from 'loadedState/actions';
import { ComponentSearchViewState } from 'viewpointBuilder/selectContextTab/types';
import {
  formattingRulesToFormattingAttributes,
  getReferenceLabelFormattingFilterAttributes,
} from 'modelInterface/filters/formattingRulesToFormattingAttributes';
import {
  CreateViewpointAttributes,
  CreateViewpointInFolderAttributes,
  isSystemFilter,
  UpdateTraversalAttributes,
  toRequestedPaths,
} from '@ardoq/api-types';
import {
  executeViewpoint as executeViewpointAction,
  openSelectedViewpoint,
  updateViewpoint,
  createViewpoint,
  createViewpointInFolder,
} from 'viewpointBuilder/actions';
import type { ViewpointBuilderState } from 'viewpointBuilder/types';
import { isTruthy } from '@ardoq/common-helpers';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  createViewpointTrackingMeta,
  viewpointBuilderTracking,
} from '../viewpointBuilderTracking';
import { getComponentSelection } from '../getComponentSelection';
import { openComponents as openComponentsAction } from '../selectContextTab/actions';
import { ViewpointFormattingProps } from '../formattingTabs/viewpointFormattingTypes';
import { labelFormattingToFilterAttributes } from 'modelInterface/filters/util';
import { ReferenceLabelSource } from '@ardoq/data-model';
import { MultiLabelFormattingProps } from '@ardoq/perspectives-sidebar';
import { extractTraversalParamsFromViewpointBuilderState } from '../utils';
import { calculateSystemFilters } from 'models/utils/filterUtils';

export type PrimaryButtonConfig = {
  primaryButtonLabel: string;
  iconName: IconName;
  isDisabled: boolean;
  primaryAction: (closeViewpointBuilder: () => void) => void;
  popoverContentForDisabledItem?: string;
};

export const getPrimaryButtonConfig = (
  state: ViewpointBuilderState,
  namespace: string
): PrimaryButtonConfig => {
  const { i18nLocale } = state;
  const context = getContext(state);
  switch (context) {
    case 'editViewpoint':
    case 'createViewpoint': {
      return getPrimaryButtonConfigToSaveViewpoint(state, namespace);
    }

    case 'loadNewViewpoint':
    case 'editSubgraph':
    case 'contextMenu': {
      return getPrimaryButtonConfigToOpenDataset(state);
    }

    case 'openViewpoint':
      return getPrimaryButtonConfigToOpenViewpoint(state);

    default:
      context satisfies never;
      return getPrimaryButtonConfigForUnknownContext(i18nLocale);
  }
};

const getOpenComponentsButtonLabel = (
  state: ComponentSearchViewState,
  i18nLocale: I18nLocale
): string => {
  const count = state.selectedComponentsCount;

  if (count === 0) {
    return t(i18nLocale, 'viewpointBuilder:primaryButton.open', 'Open');
  }
  if (count === 1) {
    return t(
      i18nLocale,
      'viewpointBuilder:primaryButton.openComponentsSingular',
      'Open 1 component'
    );
  }
  return t(
    i18nLocale,
    'viewpointBuilder:primaryButton.openComponentsPlural',
    { count },
    'Open {{count}} components'
  );
};

const getPrimaryButtonLabel = (state: ViewpointBuilderState): string => {
  const { i18nLocale } = state;
  const context = getContext(state);
  switch (context) {
    case 'editViewpoint':
      return t(
        i18nLocale,
        'viewpointBuilder:primaryButton.saveChanges',
        'Save changes'
      );
    case 'createViewpoint':
      return t(
        i18nLocale,
        'viewpointBuilder:primaryButton.saveViewpoint',
        'Save viewpoint'
      );

    case 'contextMenu':
    case 'loadNewViewpoint':
      return shouldOpenAsTraversal(state)
        ? t(
            i18nLocale,
            'viewpointBuilder:primaryButton.openDataset',
            'Open dataset'
          )
        : getOpenComponentsButtonLabel(state.searchComponentsState, i18nLocale);

    case 'editSubgraph':
      return t(
        i18nLocale,
        'viewpointBuilder:primaryButton.updateDataset',
        'Update dataset'
      );

    case 'openViewpoint':
      return t(i18nLocale, 'viewpointBuilder:primaryButton.open', 'Open');

    default:
      context satisfies never;
      return t(i18nLocale, 'viewpointBuilder:primaryButton.missing', 'Missing');
  }
};

type ViewpointBuilderStateWithMetaInfo = ViewpointBuilderState & {
  metaInfoState: { initialState: { _id: string } };
};
const isEditingSavedViewpointFromView = (
  state: ViewpointBuilderState
): state is ViewpointBuilderStateWithMetaInfo =>
  Boolean(
    getContext(state) === 'editSubgraph' &&
    state.metaInfoState.initialState?._id
  );

export const buildAttributesForSavingViewpoint = (
  state: ViewpointBuilderState
):
  | CreateViewpointAttributes
  | UpdateTraversalAttributes
  | CreateViewpointInFolderAttributes => {
  const metaInfoAttributes = state.metaInfoState.stateAsSavableAttributes;
  const {
    paths,
    filters,
    startType,
    pathMatching,
    pathCollapsingRules,
    startSetExpansion,
    startSetExpansionFromType,
  } = state.editTraversalsState.stateAsSavableAttributes;
  const { groupBys, conditionalFormatting } =
    getGroupsAndFormattingFromState(state);
  const existingViewName = state.metaInfoState.initialState?.viewName;
  const updatedViewName =
    metaInfoAttributes && existingViewName
      ? {
          viewName: toUpdatedViewpointViewId(
            metaInfoAttributes.viewName,
            existingViewName
          ),
        }
      : null;

  return {
    ...metaInfoAttributes,
    ...updatedViewName,
    paths: toRequestedPaths(paths, startSetExpansion),
    // An expansion stores the type it started from, not the `anyType` sentinel
    // the paths carry once the hierarchy node is in place.
    startType: (startSetExpansion ? startSetExpansionFromType : startType)!, // TODO AM - this one must be set at this point, we should not allow it to be null
    filters,
    groupBys,
    // @ts-expect-error We have a bit too many filter types, seems on some level they are incompatible. We should obviously fix this, but this seems a bit bigger task.
    conditionalFormatting,
    pathMatching,
    pathCollapsingRules,
    // Explicitly `null` rather than omitted: a PUT writes only the keys it
    // carries, so an omitted key would keep a stored expansion.
    startSetExpansion: startSetExpansion ?? null,
  };
};

const getMultiLabelFormattingAttributes = ({
  labelFormatting,
  showReferenceType,
}: MultiLabelFormattingProps) => [
  ...(showReferenceType
    ? [
        getReferenceLabelFormattingFilterAttributes(
          ReferenceLabelSource.REFERENCE_TYPE,
          undefined
        ),
      ]
    : [
        getReferenceLabelFormattingFilterAttributes(
          ReferenceLabelSource.NONE,
          undefined
        ),
      ]),
  ...labelFormatting.flatMap(labelFormattingToFilterAttributes),
];

const getLabelAndColorFormattingRulesToFormattingAttributes = (
  formatting: ViewpointFormattingProps
) => {
  const labelFormatting = getMultiLabelFormattingAttributes(
    formatting.multiLabelFormatting
  );

  const rulesWithUnknownValue =
    formatting.conditionalFormatting.formattingRules;

  const completeConditionalFormattingRules = rulesWithUnknownValue.filter(
    isCompleteFormattingRule
  );

  const attributeFormattingRules = formattingRulesToFormattingAttributes(
    completeConditionalFormattingRules
  ).filter(rule => isTruthy(rule) && !isSystemFilter(rule));

  const systemFilters = calculateSystemFilters({
    conditionalFormattingRules: attributeFormattingRules,
    shouldGreyOut: !formatting.conditionalFormatting.keepOriginalColor,
  });

  const formattingRules = [...attributeFormattingRules, ...systemFilters];

  return [...formattingRules, ...labelFormatting];
};

const isEmptyStartSet = (componentSearchState: ComponentSearchViewState) =>
  componentSearchState.selectedComponentsCount === 0;

/**
 * Paths gets length = 1 when entering the edit subgraph-tab, before adding any paths.
 * Its first element contains the actual user-added paths
 */
const hasTraversalPaths = (state: ViewpointBuilderState) => {
  const paths = state.editTraversalsState.stateAsSavableAttributes.paths;
  return paths.length > 0 && paths[0].length > 0;
};

/**
 * Start-set expansion (the "expand start set" checkbox) is resolved by the
 * backend traversal/execute endpoint, not the advanced-search scope-data
 * endpoint. So a dataset that only expands the start set — without any
 * user-added paths — must still be opened as a traversal, otherwise the
 * expansion is silently dropped.
 */
const hasStartSetExpansion = (state: ViewpointBuilderState) =>
  Boolean(state.editTraversalsState.stateAsSavableAttributes.startSetExpansion);

const shouldOpenAsTraversal = (state: ViewpointBuilderState) =>
  hasTraversalPaths(state) || hasStartSetExpansion(state);

const isEditingDataset = (state: ViewpointBuilderState) =>
  getContext(state) === 'editSubgraph';

/**
 * Whether the primary action writes the builder's own definition back, rather
 * than opening the selected components as a fresh dataset. Editing always does,
 * including when no paths are left: `openSelectedComponents` rebuilds from the
 * previous loaded state, which would restore the paths the user just removed.
 */
export const shouldExecuteBuilderDefinition = (state: ViewpointBuilderState) =>
  shouldOpenAsTraversal(state) || isEditingDataset(state);

const getContext = (state: ViewpointBuilderState) => {
  return state.viewpointBuilderNavigationState.context;
};

const hasMoreThanOneComponentTypeSelected = (state: ComponentSearchViewState) =>
  state.selectedStartComponentTypeNames.length > 1;

const isUpdateTraversal = (
  attributes: CreateViewpointAttributes | UpdateTraversalAttributes
): attributes is UpdateTraversalAttributes => {
  return '_id' in attributes && Boolean(attributes._id);
};

const isCreateViewpointInFolder = (
  attributes: CreateViewpointAttributes | CreateViewpointInFolderAttributes
): attributes is CreateViewpointInFolderAttributes => {
  return 'folderId' in attributes && Boolean(attributes.folderId);
};

type SaveViewpointAttributes =
  | UpdateTraversalAttributes
  | CreateViewpointAttributes
  | CreateViewpointInFolderAttributes;

const getOnSaveAction = (attributes: SaveViewpointAttributes) => {
  if (isUpdateTraversal(attributes)) {
    return updateViewpoint(attributes);
  }
  if (isCreateViewpointInFolder(attributes)) {
    return createViewpointInFolder(attributes);
  }
  return createViewpoint(attributes);
};

const getIsDisabledAndPopoverContentForSavingViewpoint = (
  state: ViewpointBuilderState
) => {
  const { i18nLocale } = state;
  const isInvalidViewpoint =
    !state.metaInfoState.canSaveViewpoint || !shouldOpenAsTraversal(state);

  const hasUnsavedCollapsedPath = Boolean(
    state.editTraversalsState.pathCollapsing.currentEditableRule
  );
  if (isInvalidViewpoint) {
    return {
      isDisabled: true,
      popoverContentForDisabledItem:
        getContext(state) === 'createViewpoint'
          ? t(
              i18nLocale,
              'viewpointBuilder:primaryButton.disabled.saveRequiresMissingFields',
              "To save your Viewpoint, you need a context component type, at least 1 reference type or 'Include descendants', and Viewpoint details."
            )
          : undefined,
    };
  }
  if (hasUnsavedCollapsedPath) {
    return {
      isDisabled: true,
      popoverContentForDisabledItem: t(
        i18nLocale,
        'viewpointBuilder:primaryButton.disabled.unsavedCollapsedPath',
        'You have an unsaved collapsed path. Please save or discard it before continuing.'
      ),
    };
  }
  return { isDisabled: false, popoverContentForDisabledItem: undefined };
};
const getPrimaryButtonConfigToSaveViewpoint = (
  state: ViewpointBuilderState,
  namespace: string
): PrimaryButtonConfig => {
  const { isDisabled, popoverContentForDisabledItem } =
    getIsDisabledAndPopoverContentForSavingViewpoint(state);
  return {
    primaryButtonLabel: getPrimaryButtonLabel(state),
    iconName: IconName.CHEVRON_RIGHT,
    isDisabled,
    popoverContentForDisabledItem,
    primaryAction: closeViewpointBuilder => {
      const attributes = buildAttributesForSavingViewpoint(state);
      dispatchAction(getOnSaveAction(attributes), namespace);
      closeViewpointBuilder();

      viewpointBuilderTracking.trackMultiLabelApply(
        state.metaInfoState.viewName,
        state.formattingState.multiLabelFormatting?.labelFormatting ?? [],
        state.metaInfoState
      );
    },
  };
};

/**
 * This should never be used, typescript should catch context that is not handled.
 */
const getPrimaryButtonConfigForUnknownContext = (
  i18nLocale: I18nLocale
): PrimaryButtonConfig => ({
  primaryButtonLabel: t(
    i18nLocale,
    'viewpointBuilder:primaryButton.missing',
    'Missing'
  ),
  iconName: IconName.NONE,
  isDisabled: true,
  primaryAction: () => {},
});

const getPrimaryButtonConfigWhenWaitingForInitialisation = (
  i18nLocale: I18nLocale
): PrimaryButtonConfig => ({
  primaryButtonLabel: t(
    i18nLocale,
    'viewpointBuilder:primaryButton.loading',
    'Loading ...'
  ),
  iconName: IconName.NONE,
  isDisabled: true,
  primaryAction: () => {},
});

const getPrimaryButtonConfigToOpenViewpoint = (
  state: ViewpointBuilderState
): PrimaryButtonConfig => {
  {
    if (!state.metaInfoState.initialState) {
      /**
       * This is only possible for opening the viewpoint builder to select components for saved viewpoint.
       * We can display the Loading button until the state is initialised with the viewpoint details.
       */
      return getPrimaryButtonConfigWhenWaitingForInitialisation(
        state.i18nLocale
      );
    }

    const { viewName, _id: viewpointId } = state.metaInfoState.initialState;

    return {
      primaryButtonLabel: getPrimaryButtonLabel(state),
      iconName: IconName.CHEVRON_RIGHT,
      isDisabled: isEmptyStartSet(state.searchComponentsState),
      primaryAction: () => {
        const { groupBys, conditionalFormatting } =
          getGroupsAndFormattingFromState(state);
        const {
          startType,
          pathCollapsingRules,
          startSetExpansion,
          startSetExpansionFromType,
        } = state.editTraversalsState.stateAsSavableAttributes;

        dispatchAction(
          openSelectedViewpoint({
            executeViewpointPayload: {
              type: 'EXECUTE_SAVED_VIEWPOINT',
              viewName,
              filters: state.editTraversalsState.initialFilters ?? {},
              viewpointId: viewpointId!,
              paths: toRequestedPaths(
                state.editTraversalsState.initialPaths ?? [],
                startSetExpansion
              ),
              componentSelection: getComponentSelection(
                state.searchComponentsState
              ),
              startType,
              groupBys,
              // @ts-expect-error We have a bit too many filter types, seems on some level they are incompatible. We should obviously fix this, but this seems a bit bigger task.
              conditionalFormatting,
              pathMatching: state.editTraversalsState.pathMatching,
              pathCollapsingRules,
              ...(startSetExpansion
                ? { startSetExpansion, startSetExpansionFromType }
                : {}),
            },
            trackingMeta: createViewpointTrackingMeta(state),
          })
        );
      },
    };
  }
};

const getIsDisabledAndPopoverContent = (state: ViewpointBuilderState) => {
  const { i18nLocale } = state;
  // Mixed start-set types are only an issue for reference hops (paths), which
  // are defined per component type. The backend happily handles a mixed-type
  // start set otherwise (e.g. the default wildcard viewpoint), so we gate this
  // on `hasTraversalPaths` rather than `shouldOpenAsTraversal` — an
  // expansion-only dataset with mixed types is allowed through.
  // NOTE: not yet addressed — to be fully precise, a mixed-type start set
  // combined with start-set expansion is only really valid when the types
  // belong to the same component-type hierarchy. We disregard that refinement
  // for now and allow any mixed-type start set with expansion enabled.
  const isInvalidStartContext =
    hasTraversalPaths(state) &&
    hasMoreThanOneComponentTypeSelected(state.searchComponentsState);
  const hasUnsavedCollapsedPath = Boolean(
    state.editTraversalsState.pathCollapsing.currentEditableRule
  );
  if (isEmptyStartSet(state.searchComponentsState)) {
    return {
      isDisabled: true,
      popoverContentForDisabledItem: undefined,
    };
  }
  if (isInvalidStartContext) {
    return {
      isDisabled: true,
      popoverContentForDisabledItem: t(
        i18nLocale,
        'viewpointBuilder:primaryButton.disabled.openRequiresSameType',
        'You can only open a dataset with references if all context components are the same type.'
      ),
    };
  }
  if (hasUnsavedCollapsedPath) {
    return {
      isDisabled: true,
      popoverContentForDisabledItem: t(
        i18nLocale,
        'viewpointBuilder:primaryButton.disabled.unsavedCollapsedPath',
        'You have an unsaved collapsed path. Please save or discard it before continuing.'
      ),
    };
  }
  return { isDisabled: false, popoverContentForDisabledItem: undefined };
};

const getPrimaryButtonConfigToOpenDataset = (
  state: ViewpointBuilderState
): PrimaryButtonConfig => {
  const { isDisabled, popoverContentForDisabledItem } =
    getIsDisabledAndPopoverContent(state);
  return {
    primaryButtonLabel: getPrimaryButtonLabel(state),
    iconName: IconName.CHEVRON_RIGHT,
    isDisabled,
    popoverContentForDisabledItem,
    primaryAction: closeViewpointBuilder => {
      if (shouldExecuteBuilderDefinition(state)) {
        executeViewpoint(state);
      } else {
        openSelectedComponents(state);
      }
      viewpointBuilderTracking.trackOpenDataSet(state);
      viewpointBuilderTracking.trackDatasetApplied(state);
      closeViewpointBuilder();
    },
  };
};

export const executeViewpoint = (state: ViewpointBuilderState) => {
  if (isEditingSavedViewpointFromView(state)) {
    const { groupBys, conditionalFormatting } =
      getGroupsAndFormattingFromState(state);
    const { _id: viewpointId, viewName } = state.metaInfoState.initialState;
    const savableAttributes =
      state.editTraversalsState.stateAsSavableAttributes;
    const loadedStateHash =
      state.metaInfoState.initialState?.loadedStateHash ?? undefined;

    dispatchAction(
      executeViewpointAction({
        type: 'EXECUTE_SAVED_VIEWPOINT',
        componentSelection: getComponentSelection(state.searchComponentsState),
        ...savableAttributes,
        paths: toRequestedPaths(
          savableAttributes.paths,
          savableAttributes.startSetExpansion
        ),
        groupBys,
        viewpointId,
        loadedStateHash,
        viewName,
        // @ts-expect-error We have a bit too many filter types, seems on some level they are incompatible. We should obviously fix this, but this seems a bit bigger task.
        conditionalFormatting,
      })
    );

    return;
  }

  dispatchAction(
    executeViewpointAction({
      type: 'EXECUTE_UNSAVED_VIEWPOINT',
      ...extractTraversalParamsFromViewpointBuilderState(state),
      loadedStateHash:
        state.metaInfoState.initialState?.loadedStateHash ?? undefined,
    })
  );
};

const openSelectedComponents = (state: ViewpointBuilderState) => {
  const loadedStateHash = state.metaInfoState.initialState?.loadedStateHash;
  if (loadedStateHash) {
    dispatchAction(
      setTraversalContextComponent({
        loadedStateHash,
        componentSelection: getComponentSelection(state.searchComponentsState),
      })
    );
  } else {
    dispatchAction(
      openComponentsAction({
        componentSelection: getComponentSelection(state.searchComponentsState),
      })
    );
  }
};

const getGroupsAndFormattingFromState = (state: ViewpointBuilderState) => {
  const groupBys = state.groupingsState.groupingRules.map(
    perspectiveGroupingRuleToGroupingRule
  );

  const conditionalFormatting =
    getLabelAndColorFormattingRulesToFormattingAttributes(
      state.formattingState
    );
  return { groupBys, conditionalFormatting };
};
